<template>
  <VCalendarDatePicker
    v-model.range="date"
    :rows="isLargeScreen ? 1 : 2"
    :columns="isLargeScreen ? 2 : 1"
    v-bind="{ ...attrs, ...$attrs, attributes }"
    :disabled-dates="filteredDisableDates"
    :expanded="!isLargeScreen"
    @dayclick="onDayClick"
    @drag="onStartSelected($event)"
  >
  </VCalendarDatePicker>
</template>

<script setup lang="ts">
import { DatePicker as VCalendarDatePicker } from '@angelblanco/v-calendar'
import type { DatePickerRangeObject } from '@angelblanco/v-calendar/dist/types/src/use/datePicker.js'
import '@angelblanco/v-calendar/dist/style.css'
import { LangList } from '~~/modules/lang/entities/Lang'

const { isLargeScreen } = useResponsive()

defineOptions({ inheritAttrs: false })

interface propsInterface {
  modelValue?: DatePickerRangeObject | null
  disableDates?: Array<{ start: string; end: string }> | undefined
}

const props = withDefaults(defineProps<propsInterface>(), {
  modelValue: null,
})

const selectedStartDate = ref<Date | null>(null)

const emit = defineEmits(['update:model-value', 'close', 'invalid-range'])

const onStartSelected = (dates: DatePickerRangeObject) => {
  selectedStartDate.value = dates.start as Date
}

const filteredDisableDates = computed(() => {
  if (!selectedStartDate.value || !props.disableDates?.length) {
    return props.disableDates
  }

  const startDate = new Date(selectedStartDate.value)

  let firstDisabledAfterStart: { start: string; end: string } | undefined

  for (const d of props.disableDates) {
    const disabledDate = new Date(d.start)
    if (disabledDate > startDate) {
      firstDisabledAfterStart = d
      break
    }
  }

  return firstDisabledAfterStart
    ? props.disableDates.filter(d => d.start !== firstDisabledAfterStart.start)
    : props.disableDates
})

const attributes = ref([
  {
    key: 'today',
    dates: new Date(),
  },
  {
    key: 'disabled-custom',
    highlight: { fillMode: 'light', color: 'red' },
    content: 'red',
    dates: filteredDisableDates,
  },
])
const validateRange = (value: DatePickerRangeObject) => {
  if (value.start && value.end) {
    const startDate = new Date(value.start as Date)
    const endDate = new Date(value.end as Date)

    if (startDate.getTime() === endDate.getTime()) {
      return {
        start: startDate,
        end: new Date(startDate.getTime() + 24 * 60 * 60 * 1000),
      }
    }

    return { start: startDate, end: endDate }
  }
  return value
}

const date = computed({
  get: () => props.modelValue,
  set: value => {
    const validatedValue = validateRange(value as DatePickerRangeObject)
    emit('update:model-value', validatedValue)
    emit('close')
  },
})

const attrs = {
  transparent: true,
  borderless: true,
  color: 'primary',
  locale: LangList['es-ES'],
  'first-day-of-week': 2,
  'min-date': new Date(),
  'max-date': new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
}

function onDayClick(_: any, event: MouseEvent): void {
  const target = event.target as HTMLElement
  target.blur()
}
</script>

<style lang="postcss">
.vc-pane-container {
  font-family: var(--font-sans);
}

.vc-header {
  margin-bottom: var(--spacing-2.5);
}

.vc-header .vc-title {
  color: var(--color-neutral-900);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
}

.vc-weekday {
  color: var(--color-neutral-900);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
}

.vc-weeks {
  padding-left: var(--spacing-5);
  padding-right: var(--spacing-5);
}

.vc-nav-item {
  color: var(--color-neutral-900);
}

.vc-nav-item.is-active {
  color: var(--color-neutral-900);
}

.vc-day-content {
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  background-color: transparent;
  color: var(--color-neutral-900);
}

.vc-day-content:hover {
  font-weight: var(--font-weight-bold);
  border: 2px solid var(--color-primary-600);
}

.vc-disabled:hover {
  font-weight: var(--font-weight-regular);
  border: none;
}

.vc-highlight-content-outline {
  font-weight: var(--font-weight-bold);
  background-color: var(--color-primary-800);
  color: var(--color-white);
}

.vc-highlight-content-outline:hover {
  font-weight: var(--font-weight-bold);
  background-color: var(--color-primary-800);
  color: var(--color-white);
}

.vc-highlight-content-solid {
  font-weight: var(--font-weight-bold);
  background-color: var(--color-primary-800);
  color: var(--color-white);
}

.vc-highlight-content-solid:hover {
  font-weight: var(--font-weight-bold);
  background-color: var(--color-primary-800);
  color: var(--color-white);
}

.vc-highlight-bg-outline {
  display: none;
}

.vc-highlight-content-light {
  font-weight: var(--font-weight-bold);
  background-color: var(--color-primary-200);
  color: var(--color-primary-900);
}

.vc-day-content.vc-disabled {
  background-color: var(--color-neutral-100);
  color: var(--color-neutral-400);
}

.vc-disabled.vc-red {
  font-weight: var(--font-weight-regular);
  background-color: var(--color-red-100);
  color: var(--color-red-700);
  text-decoration: line-through;
}
</style>
